import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { AddressSelectionDialogComponent } from '../dialogs/address-selection-dialog/address-selection-dialog.component';
import { CopyLinesDialogComponent } from '../dialogs/copy-lines-dialog/copy-lines-dialog.component';
import { EntityBreakDialogComponent } from '../dialogs/entity-break-dialog/entity-break-dialog.component';
import { EntityCancelDialogComponent } from '../dialogs/entity-cancel-dialog/entity-cancel-dialog.component';
import { LeadTimeDialogComponent } from '../dialogs/lead-time-dialog/lead-time-dialog.component';
import { LineSummaryDialogComponent } from '../dialogs/line-summary-dialog/line-summary-dialog.component';
import { RiserChartDialogComponent } from '../dialogs/riser-chart-dialog/riser-chart-dialog.component';
import { RiserDialogComponent } from '../dialogs/riser-dialog/riser-dialog.component';
import { SalesRepDialogComponent } from '../dialogs/sales-rep-dialog/sales-rep-dialog.component';
import { WarrantyTextDialogComponent } from '../dialogs/warranty-text-dialog/warranty-text-dialog.component';
import { SkyportContactDialogComponent } from '../dialogs/skyport-contact-dialog/skyport-contact-dialog.component';
import { Entity, EntityKey } from '../entity/entity';
import { EntityLineHeader } from '../entity/entity-line-header';
import { PersonLogin, RoleName } from '../person/person-login';
import { SelectionItem } from '../selections/selection-item';
import { EntityService } from '../services/entity-service';
import { OrderService } from '../services/order-service';
import { OrganizationService } from '../services/organization-service';
import { PersonLoginService } from '../services/person-login-service';
import { PersonService } from '../services/person-service';
import { SelectionService } from '../services/selection-service';
import { SfrManagementService } from '../services/sfr-management-service';
import { DateUtils } from '../utils/date-utils';
import { StringUtils } from '../utils/string-utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProcessingStatusDialogComponent } from '../dialogs/processing-status-dialog/processing-status-dialog.component';
import { EntityComplexity } from '../model/entity-complexity';
import { EntityComplexityService } from '../services/entity-complexity.service';
import { ComplexityInfoDialogComponent } from '../dialogs/complexity-info-dialog/complexity-info-dialog.component';
import { ExtendedWarrantyUtils } from '../utils/extended-warranty-utils';
import { EntityLineUtils } from '../utils/entity-line-utils';
import { QewlDialogComponent } from '../dialogs/qewl-dialog/qewl-dialog.component';
import { DialogService } from '../services/dialog.service';
import { EnterContactDialogComponent } from '../dialogs/enter-contact-dialog/enter-contact-dialog.component';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { CurrencyPipe, DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IecTaskService } from '../services/iec-task-service';
import { IecTask } from '../tasks/iec-task';
import { EntityUtils } from '../utils/entity-utils';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { EntitySaveResponse } from '../entity/entity-save-response';
import { IecLeadTime } from '../model/iec-lead-time';
import { LeadTimeService } from '../services/lead-time.service';
import { ProjectProductType } from '../enums/project-product-type';
import { BrandID } from '../enums/brand-id';
import { FpaService } from '../services/fpa-service';
import { EntityKeyUtils } from '../utils/entity-key-utils';
import { WarrantyLineSummaryDialogComponent } from '../dialogs/warranty-line-summary-dialog/warranty-line-summary-dialog.component';
import { LeadTimeUtils } from '../utils/lead-time-utils';
import { FileUtils } from '../utils/file-utils';
import { DocumentService } from '../services/document-service';
import { EntityLineRevisionChange } from '../model/entity-line-revision-change';
import { EntityLineRevisionChangeService } from '../services/entity-line-revision-change.service';
import { MatFormField, MatHint } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { UnrestrictedEntityUpdate } from '../model/unrestricted-entity-update';

@Component({
  selector: 'app-order-detail',
  templateUrl: './order-detail.component.html',
  styleUrls: ['./order-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButton,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatTooltip,
    RouterLink,
    MatProgressSpinner,
    NgClass,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    DecimalPipe,
    CurrencyPipe,
    DatePipe,
    MatFormField,
    MatHint,
    MatInput,
  ],
})
export class OrderDetailComponent implements OnChanges, OnInit {
  private readonly documentService = inject(DocumentService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly selectionService = inject(SelectionService);
  private readonly organizationService = inject(OrganizationService);
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly sfrService = inject(SfrManagementService);
  private readonly orderService = inject(OrderService);
  private readonly personService = inject(PersonService);
  private readonly entityComplexityService = inject(EntityComplexityService);
  private readonly dialogService = inject(DialogService);
  private readonly taskService = inject(IecTaskService);
  private readonly leadTimeService = inject(LeadTimeService);
  private readonly fpaService = inject(FpaService);
  private readonly entityLineRevisionChangeService = inject(EntityLineRevisionChangeService);
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });
  private personLogin!: PersonLogin;
  private readonly canSubmitFpa = signal(false);
  private readonly canCancelFpaReview = signal(false);
  public readonly isRepUser = signal<boolean>(undefined!);
  public controllingOrgs!: SelectionItem[];
  public readonly sellingOrgDescription = signal<string>(undefined!);
  public readonly order = signal<Entity>(undefined!);
  public readonly associatedOrders = signal<EntityKey[]>(undefined!);
  public readonly isBusy = signal<boolean>(undefined!);
  public readonly waitMessage = signal<string>('Please Wait...');
  public readonly customers = signal<SelectionItem[]>(undefined!);
  public readonly repOffices = signal<SelectionItem[]>(undefined!);
  public readonly releaseTypes = signal<SelectionItem[]>(undefined!);
  public readonly fobs = signal<SelectionItem[]>(undefined!);
  public readonly paymentTerms = signal<SelectionItem[]>(undefined!);
  public readonly salesReps = signal<SelectionItem[]>(undefined!);
  public readonly outsideSalesReps = signal<SelectionItem[]>(undefined!);
  public readonly iecContacts = signal<SelectionItem[]>(undefined!);
  public readonly leadTimes = signal<SelectionItem[]>(undefined!);
  public readonly salesChannels = signal<SelectionItem[]>(undefined!);
  public readonly useTypes = signal<SelectionItem[]>(undefined!);
  public readonly sAndHChargeTypes = signal<SelectionItem[]>(undefined!);
  public readonly shipVias = signal<SelectionItem[]>(undefined!);
  public readonly deliveryCountries = signal<SelectionItem[]>(undefined!);
  public readonly marketSegments = signal<SelectionItem[]>(undefined!);
  public readonly projectTypes = signal<SelectionItem[]>(undefined!);
  public readonly specTypes = signal<SelectionItem[]>(undefined!);
  public readonly applicationTypes = signal<SelectionItem[]>(undefined!);
  public readonly milestones = signal<SelectionItem[]>(undefined!);
  public readonly saveMessages = signal<string[]>(undefined!);
  public selectedLines: EntityLineHeader[] = [];
  public selectionStartIndex = -1;
  public readonly createdBy = signal<string>(undefined!);
  public readonly updatedBy = signal<string>(undefined!);
  public readonly skyportContactMissing = signal(false);
  public readonly taggingRoomMissing = signal(false);
  public readonly mockupPending = signal(true);
  private readonly currentLeadTime = signal('');
  readonly creditExceptionDescription = signal<string>(undefined!);
  readonly entityComplexity = signal<EntityComplexity>(undefined!);
  public isConsolidatedView!: boolean;
  public consolidatedLines!: EntityLineHeader[];
  readonly isModReviewTaskOpen = signal<boolean>(undefined!);
  readonly selectedBrand = signal<string>(undefined!);
  MAX_QUICK_SHIP_QTY = 4;
  readonly errorMessages = signal<string[]>([]);
  protected readonly ProjectProductType = ProjectProductType;

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessages.update(current => [...current, StringUtils.getErrorMessage(error)]);
  }

  public generateConsolidatedLines(): void {
    if (this.order().lines && this.order().lines.length > 0 && !this.isConsolidatedView) {
      this.isConsolidatedView = true;
      this.consolidatedLines = structuredClone(this.order().lines);
      this.consolidatedLines = this.consolidatedLines.reduce(function (acc: EntityLineHeader[], curr: EntityLineHeader) {
        if (!acc.some((x) => x.product === curr.product && x.description === curr.description)) {
          acc.push(curr);
        } else {
          const foundElh: EntityLineHeader = acc.find((x) => x.product === curr.product && x.description === curr.description)!;
          foundElh.quantity = foundElh.quantity + curr.quantity;
        }
        return acc;
      }, []);
    } else {
      this.isConsolidatedView = false;
    }
  }

  private initialize(): void {
    this.loadSelectionLists();
    this.initializeFromRouteParams();
  }

  private loadOrderDetails(result: Entity): void {
    if (!result.frozen && result.id.entityRevision > 0 && 'Requires Review' === result.reviewStatus) {
      const messages: string[] = [];
      messages.push('This revision has not yet been submitted for review.');
      messages.push("Please click the 'Submit for Review' button after all necessary changes have been made");
      this.dialogService.showGenericMessageDialog(messages);
    }
    this.selectedBrand.set(EntityUtils.createBrandSelectionKey(result.brandID, result.projectProductType));
    this.taskService.getActiveTasksByNameAndEntity('Mod Order Review', result.id.entityId, result.id.entityRevision).subscribe({
      next: (modReviewTasks: IecTask[]) => {
        this.isModReviewTaskOpen.set((modReviewTasks?.length || 0) > 0);
        this.order.set(result);
        this.updateFpaStatusFlags();
        this.orderService.getReleases(this.order().id).subscribe((result) => (this.associatedOrders.set(result)));
        this.personService.getByUserLogin(this.order().createUser).subscribe((result) => {
          if (result) {
            this.createdBy.set(result.description);
          } else {
            this.createdBy.set(this.order().createUser);
          }
        });
        this.entityComplexityService.getEntityComplexity(this.order().id).subscribe((complexity) => (this.entityComplexity.set(complexity)));
        this.loadUpdateUser();
        this.loadSellingOrganizationSelections();
        if (this.order().leadTimeCode === 'PTS') {
          this.selectionService.getPartsCustomerServiceReps().subscribe((results) => {
            this.iecContacts.set(results);
            if (this.iecContacts() && this.iecContacts().length > 0) {
              this.order().iecContactId = this.iecContacts()[0].code;
              this.order().iecContactDescription = this.iecContacts()[0].description;
            }
          });
        }
        this.orderService.getCreditExceptionDescription(this.order().creditExceptionCode).subscribe((creditException) => (this.creditExceptionDescription.set(creditException)));
        this.mockupPending.set(!!(this.order().erpStatus && this.order().erpStatus === 'MocAppReq'));
        this.isBusy.set(false);
        this.waitMessage.set('Please Wait...');
      },
      error: (error) => this.handleError(error),
    });
  }

  private loadUpdateUser(): void {
    this.personService.getByUserLogin(this.order().updateUser).subscribe((result) => {
      if (result) {
        this.updatedBy.set(result.description);
      } else {
        this.updatedBy.set(this.order().updateUser);
      }
    });
  }

  public save(): void {
    this.resetStatus();
    this.orderService.save(this.order()).subscribe({
      next: (result) => {
        this.order.set(result.updated);
        this.isBusy.set(false);
        this.saveMessages.set(result.messages);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  private async requestedShipDateChecks(): Promise<void> {
    const leadTime: IecLeadTime = await firstValueFrom<IecLeadTime>(this.leadTimeService.getLeadTimeByProgram(this.order().leadTimeCode));
    const currentDateWithLeadTime = new Date(new Date().setDate(new Date().getDate() + leadTime.leadTimeDays));
    if (DateUtils.formatDisplay(new Date(this.order().requestedShipDate)) < new Date(currentDateWithLeadTime.toLocaleDateString())) {
      this.errorMessages.update(current => [...current, 'Invalid Requested Ship Date.  Earliest possible requested ship date is: ' + currentDateWithLeadTime.toISOString().split('T')[0]]);
    }
  }

  private quickShipQuantityChecks() {
    if (EntityUtils.isCommercialQuickShip(this.order().leadTimeCode) || EntityUtils.isResidentialQuickShip(this.order().leadTimeCode)) {
      for (const entityLine of this.order().lines) {
        if (entityLine.configured && entityLine.quantity > this.MAX_QUICK_SHIP_QTY) {
          this.errorMessages.update(current => [...current, 'Line ' + entityLine.lineId + ' exceeds the Quick Build quantity allowed of ' + this.MAX_QUICK_SHIP_QTY]);
        }
      }
    }
  }

  private async dataChecks(): Promise<void> {
    this.quickShipQuantityChecks();
    await this.requestedShipDateChecks();
    if (this.errorMessages().length === 0) {
      if (this.order().id.entityRevision > 0) {
        const changes = await firstValueFrom<EntityLineRevisionChange[]>(this.dialogService.showEntityRevisionChangesDialog(this.order().id));
        if ((changes?.length || 0) === 0) {
          this.errorMessages.update(current => [...current, 'Order Revisions cannot be submitted without a description for changes']);
        } else {
          await firstValueFrom<string>(this.entityLineRevisionChangeService.saveChanges(changes));
        }
      }
    }
  }

  public submitForReview(): void {
    this.errorMessages.set([]);
    this.dataChecks().then(() => {
      if (this.errorMessages().length === 0) {
        this.resetStatus();
        if ('MOK' === this.order().leadTimeCode && this.order().parentId != null) {
          this.order().parentId = new EntityKey();
          this.order().parentId.entityId = this.order().associatedOrderId;
          this.order().parentId.entityRevision = this.order().associatedOrderRevision;
        }
        if ('MR' === this.order().releaseTypeId) {
          this.proceedWithReview();
        } else {
          this.proceedWithReview();
        }
      }
    });
  }

  private proceedWithReview(): void {
    this.orderService.submitForReview(this.order().id).subscribe({
      next: (result) => {
        this.order.set(result);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
      complete: () => (this.isBusy.set(false)),
    });
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessages.set([]);
    this.saveMessages.set(null!);
    this.skyportContactMissing.set(false);
    this.taggingRoomMissing.set(false);
  }

  backToOrderMaster(): void {
    this.router.navigate(['orders']);
  }

  private loadSelectionLists(): void {
    this.selectionService.getControllingOrgs().subscribe((results) => (this.repOffices.set(results)));
    this.selectionService.getContractTypes().subscribe((results) => (this.releaseTypes.set(results)));
    this.selectionService.getDeliveryTerms().subscribe((results) => (this.fobs.set(results)));
    this.selectionService.getPaymentTerms().subscribe((results) => (this.paymentTerms.set(results)));
    this.selectionService.getCustomerServiceReps().subscribe((results) => (this.iecContacts.set(results)));
    this.selectionService.getFreightTerms().subscribe((results) => (this.shipVias.set(results)));
    this.selectionService.getCountryCodes().subscribe((results) => (this.deliveryCountries.set(results)));
    this.selectionService.getMarketSegments().subscribe((results) => (this.marketSegments.set(results)));
    this.selectionService.getProjectTypes().subscribe((results) => (this.projectTypes.set(results)));
    this.selectionService.getNewExtendCodes().subscribe((results) => (this.specTypes.set(results)));
    this.selectionService.getMilestones().subscribe((results) => (this.milestones.set(results)));
    this.selectionService.getApplicationTypes().subscribe((results) => (this.applicationTypes.set(results)));
  }

  public loadSellingOrganizationSelections(): void {
    this.customers.set(null!);
    this.salesReps.set(null!);
    this.outsideSalesReps.set(null!);
    this.leadTimes.set(null!);
    this.salesChannels.set(null!);
    this.useTypes.set(null!);
    this.sAndHChargeTypes.set(null!);
    if (this.order() && this.order().repOfficeId) {
      this.isBusy.set(true);
      this.organizationService.getOrganization(this.order().repOfficeId).subscribe((sellingOrg) => {
        this.sellingOrgDescription.set(sellingOrg.description);
        this.selectionService.getCustomers(this.order().repOfficeId).subscribe((results) => {
          if (results.filter((result) => result.code === this.order().repOfficeId).length === 0) {
            const rep: SelectionItem = new SelectionItem();
            rep.code = sellingOrg.orgUnitId;
            rep.description = sellingOrg.description;
            results.unshift(rep);
          }
          this.customers.set(results);
        });

        this.loadSalesReps();

        this.selectionService.getOrganizationLeadTimes(this.order().repOfficeId, this.order().brandID, this.order().projectProductType).subscribe((results) => {
          this.leadTimes.set(results);
          this.leadTimes.set(this.leadTimes().filter((item) => item.description.toUpperCase() !== 'WARRANTY'));
          this.leadTimes.set(LeadTimeUtils.selectableLeadTimes(this.leadTimes(), this.order().leadTimeCode));
        });
        this.selectionService.getOrganizationChannels(this.order().repOfficeId).subscribe((results) => {
          this.salesChannels.set(results);
        });
        this.selectionService.getOrganizationExpediteCodes(this.order().repOfficeId).subscribe((results) => {
          this.useTypes.set(results);
        });
        this.selectionService.getOrganizationTransportationTerms(this.order().repOfficeId).subscribe((results) => (this.sAndHChargeTypes.set(results)));
        this.currentLeadTime.set(this.order().leadTimeCode);
        this.isBusy.set(false);
      });
    }
  }

  loadSalesReps(): void {
    this.selectionService.getOrganizationPeople(this.order().repOfficeId, this.order().brandID).subscribe({
      next: (results) => {
        this.salesReps.set(results);
        this.outsideSalesReps.set(results);
      },
    });
  }

  public userHasRole(roleName: string): boolean {
    return this.personLoginService.hasRole(RoleName[roleName as keyof typeof RoleName]);
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.isBusy.set(true);
      this.personLogin = this.personLoginService.getCachedLogin()!;
      const primaryOrgUnit = this.personLogin.person.primaryOrgUnitId;
      if (primaryOrgUnit && primaryOrgUnit.length > 0) {
        this.organizationService.getOrganization(primaryOrgUnit).subscribe((organization) => {
          this.isRepUser.set(organization.typeCode === 'IREP' || organization.typeCode === 'OREP');
          this.initialize();
        });
      } else {
        this.isRepUser.set(false);
        this.initialize();
      }
    }
  }

  public formatDisplayDate(input: Date): Date {
    return DateUtils.formatDisplay(input);
  }

  public setSfrPriceDate(newValue: Date) {
    this.order().sfrPriceBasisDate = DateUtils.formatOutput(newValue);
  }

  public setShipDate(newValue: Date) {
    this.order().estimatedScheduleDate = DateUtils.formatOutput(newValue);
  }

  public setPriceBasisDate(newValue: Date) {
    this.order().priceBasisDate = DateUtils.formatOutput(newValue);
  }

  public setRequestedShipDate(newValue: Date) {
    this.order().requestedShipDate = DateUtils.formatOutput(newValue);
  }

  public multiplier(): void {
    this.resetStatus();
    if (StringUtils.isBlank(this.order().overrideShipToStateCode) && !LeadTimeUtils.isPartsOrWarrantyLeadTime(this.order().leadTimeCode)) {
      this.askForStateAndRunMultiplier().then(() => (this.isBusy.set(false)));
    } else {
      this.runMultiplier().then(() => (this.isBusy.set(false)));
    }
  }

  private async askForStateAndRunMultiplier(): Promise<void> {
    const stateCode: string = await firstValueFrom<string>(this.dialogService.showStateSelectionDialog());
    if (StringUtils.isNotBlank(stateCode)) {
      this.order().overrideShipToStateCode = stateCode;
      await firstValueFrom<EntitySaveResponse>(this.orderService.save(this.order()));
      await this.runMultiplier();
    }
  }

  private async runMultiplier(): Promise<void> {
    await firstValueFrom<Entity>(this.orderService.runMultiplier(this.order().id));
    this.order.set(await firstValueFrom<Entity>(this.entityService.getEntityByKey(this.order().id)));
    this.loadUpdateUser();
  }

  public openConfigurator(): void {
    this.router.navigate(['configurator', this.order().id.entityName, this.order().id.entityId, this.order().id.entityRevision]);
  }

  showConfigurator(): boolean {
    return !LeadTimeUtils.isStockProgram(this.order().leadTimeCode);
  }

  showCancel(): boolean {
    return !this.isStockProgram() || this.userHasRole('IT');
  }

  isStockProgram(): boolean {
    return LeadTimeUtils.isStockProgram(this.order().leadTimeCode);
  }

  leadTimeDescription(): string {
    return this.leadTimes()?.find((leadTime) => leadTime.code === this.order().leadTimeCode)?.description ?? this.order().leadTimeCode;
  }

  productColumn(line: EntityLineHeader): string {
    return EntityUtils.isStockUnitLine(this.order(), line) && line.itemNumber ? line.itemNumber : line.product;
  }

  isIncompleteLine(line: EntityLineHeader): boolean {
    return EntityUtils.isIncompleteLine(this.order(), line);
  }

  isSingleStockUnitSelected(): boolean {
    return this.selectedLines.length === 1 && EntityUtils.isStockUnitLine(this.order(), this.selectedLines[0]);
  }

  showStockLineSpecifications(): void {
    if (this.isSingleStockUnitSelected()) {
      this.dialogService.showStockLineSpecificationsDialog(this.selectedLines[0]);
    }
  }

  private showRunMultiplierSnackBar(): void {
    if (!this.order().frozen && this.order().updateAllowed) {
      const snackBarRef = this.snackBar.open('Please run the multipler to see updated pricing', 'Run Now', {
        duration: 15000,
      });
      snackBarRef.onAction().subscribe(() => this.multiplier());
      snackBarRef.afterDismissed().subscribe((dismissed) => {
        if (!dismissed.dismissedByAction) {
          setTimeout(() => (this.saveMessages.set(new Array('Please run the multipler to see updated pricing'))), 0);
        }
      });
    }
  }

  public addRisers(): void {
    const riserDialog = this.dialog.open(RiserDialogComponent, {
      disableClose: true,
      width: '1300px',
      height: '640px',
      data: { entity: this.order() },
    });
    riserDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.errorMessages.set([]);
        this.order.set(result);
        this.showRunMultiplierSnackBar();
      }
    });
  }

  public openLeadTimeDialog(): void {
    const leadTimeDialog = this.dialog.open(LeadTimeDialogComponent, {
      disableClose: true,
      data: { leadTimes: this.leadTimes(), entityKey: this.order().id },
    });
    leadTimeDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.errorMessages.set([]);
        this.showRunMultiplierSnackBar();
        this.order().lines = result;
      }
    });
  }

  public showLineSummary(): void {
    if (this.selectedLines && this.selectedLines.length === 1) {
      const selectedLine = this.selectedLines[0];
      const lineSummaryDialog = this.dialog.open(LineSummaryDialogComponent, {
        disableClose: true,
        data: { line: JSON.parse(JSON.stringify(selectedLine)) },
      });
      lineSummaryDialog.afterClosed().subscribe((result) => {
        if (result) {
          this.isBusy.set(true);
          this.order().lines.filter((line) => line.lineId === result.lineId)[0].quantity = result.quantity;
          this.isBusy.set(false);
        }
      });
    }
  }

  public warrantyLineSummaryActive(): boolean {
    return this.order().leadTimeCode === 'WAR' || this.order().leadTimeCode === 'CPW' || this.order().leadTimeCode === 'RPW';
  }

  public showWarrantyLineSummary(): void {
    if (this.selectedLines && this.selectedLines.length === 1 && this.warrantyLineSummaryActive()) {
      const selectedLine = this.selectedLines[0];
      const lineWarrantySummaryDialog = this.dialog.open(WarrantyLineSummaryDialogComponent, {
        disableClose: true,
        data: { line: JSON.parse(JSON.stringify(selectedLine)), dealerAddressId: this.order().dealerAddressId },
      });
      lineWarrantySummaryDialog.afterClosed().subscribe((result) => {
        if (result) {
          this.isBusy.set(true);
          this.order().lines.filter((line) => line.lineId === result.lineId)[0].quantity = result.quantity;
          this.isBusy.set(false);
        }
      });
    }
  }

  public selectLineRow(clickEvent: MouseEvent, line: EntityLineHeader): void {
    if (clickEvent.button !== 0) {
      return;
    }
    if (!clickEvent.ctrlKey && !clickEvent.shiftKey) {
      this.selectedLines = [];
      this.selectedLines.push(line);
      this.selectionStartIndex = this.order().lines.indexOf(line);
    } else {
      if (clickEvent.ctrlKey) {
        this.toggleLineSelection(line);
        this.selectionStartIndex = this.order().lines.indexOf(line);
      }
      if (clickEvent.shiftKey) {
        this.selectedLines = [];
        const clickedIndex = this.order().lines.indexOf(line);
        const first = Math.min(clickedIndex, this.selectionStartIndex);
        const last = Math.max(clickedIndex, this.selectionStartIndex);
        for (let index = first; index <= last; index++) {
          this.selectedLines.push(this.order().lines[index]);
        }
      }
    }
  }

  private toggleLineSelection(line: EntityLineHeader): void {
    const index = this.selectedLines.indexOf(line, 0);
    if (index > -1) {
      this.selectedLines.splice(index, 1);
    } else {
      this.selectedLines.push(line);
    }
  }

  public isLineSelected(line: EntityLineHeader): boolean {
    return this.selectedLines.indexOf(line, 0) > -1;
  }

  public getSelectedLineCount(): number {
    return this.selectedLines.length;
  }

  isDiscontinued(): boolean {
    if (!this.selectedLines || this.selectedLines.length === 0) {
      return false;
    }
    return this.selectedLines.filter((line) => EntityLineUtils.hasDiscontinuedProduct(line)).length > 0;
  }

  public addParts(): void {
    this.dialogService.showPartsDialog(this.order()).subscribe({
      next: (result) => {
        if (result) {
          this.order.set(result);
          this.showRunMultiplierSnackBar();
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  public copyLines(): void {
    const copyLinesDialog = this.dialog.open(CopyLinesDialogComponent, {
      disableClose: true,
      width: '1360px',
      height: '640px',
      data: { lines: JSON.parse(JSON.stringify(this.selectedLines)) },
    });
    copyLinesDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isBusy.set(true);
        this.router.navigate(['quotes', result.id.entityId, result.id.entityRevision]);
      }
    });
  }

  public selectAddress(): void {
    const addressDialog = this.dialog.open(AddressSelectionDialogComponent, {
      data: { entity: JSON.parse(JSON.stringify(this.order())), personLogin: this.personLogin },
    });
    addressDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.order.set(result);
      }
    });
  }

  public showRequestFPA(): boolean {
    return !EntityUtils.isApprovedOrFrozen(this.order());
  }

  public fpaManagement(): void {
    if (this.errorMessages().length === 0) {
      this.resetStatus();
      this.entityService.hasMissingRiserData(this.order().id).subscribe({
        next: (invalidRiserData: string) => {
          if (StringUtils.equalsIgnoreCase('true', invalidRiserData)) {
            this.errorMessages.update(current => [...current, 'Please upload risers via the Riser Chart button and ensure Project Defaults are completed before requesting a FPA.']);
            this.isBusy.set(false);
          } else {
            if ('Approved' != this.order().approvalStatus && !this.order().frozen) {
              this.orderService.runMultiplier(this.order().id).subscribe({
                next: (updated: Entity) => {
                  this.order.set(updated);
                  if (LeadTimeUtils.isQuickShip(this.order().leadTimeCode)) {
                    this.errorMessages.update(current => [...current, LeadTimeUtils.QUICK_SHIP_FPA_MESSAGE]);
                    this.isBusy.set(false);
                  } else {
                    this.router.navigate(['fpa/CONTRACT/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
                  }
                },
                error: (error) => this.handleError(error),
              });
            } else if (LeadTimeUtils.isQuickShip(this.order().leadTimeCode)) {
              this.errorMessages.update(current => [...current, LeadTimeUtils.QUICK_SHIP_FPA_MESSAGE]);
              this.isBusy.set(false);
            } else {
              this.router.navigate(['fpa/CONTRACT/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
            }
          }
        },
      });
    }
  }

  public viewQuote(): void {
    this.router.navigate(['quotes', this.order().associatedQuoteId, this.order().associatedQuoteRevision]);
  }

  public viewBlanket(): void {
    this.router.navigate(['orders', this.order().associatedOrderId, this.order().associatedOrderRevision]);
  }

  public viewOrder(id: EntityKey): void {
    this.router.navigate(['orders', id.entityId, id.entityRevision]);
  }

  public viewDocuments(): void {
    this.router.navigate(['documents/' + this.order().id.entityName + '/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
  }

  public viewReleaseSummary(): void {
    this.router.navigate(['releaseSummary/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
  }

  public viewEntityBreaks(): void {
    const breaksDialog = this.dialog.open(EntityBreakDialogComponent, {
      data: { entityKey: this.order().id, personLogin: this.personLogin, brandID: this.order().brandID },
    });
    breaksDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.showRunMultiplierSnackBar();
      }
    });
  }

  public cancelSelectedLines(): void {
    const cancelDialog = this.dialog.open(EntityCancelDialogComponent, {
      data: { entityKey: this.order().id, entityLines: this.selectedLines },
    });
    cancelDialog.afterClosed().subscribe((cancelReason) => {
      if (cancelReason) {
        this.isBusy.set(true);
        this.orderService.cancelLines(cancelReason, this.selectedLines).subscribe({
          next: (updated) => {
            this.order.set(updated);
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  public showSalesRep(): void {
    if (this.order().salesRepId && this.order().salesRepId.length > 0) {
      this.personService.getContactPerson(this.order().salesRepId).subscribe((result) => {
        const salesRep = result;
        if (salesRep) {
          this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '400px',
            height: '150px',
            data: { rep: salesRep },
          });
        }
      });
    }
  }

  public showIecContact(): void {
    if (this.order().iecContactId && this.order().iecContactId.length > 0) {
      this.personService.getContactPerson(this.order().iecContactId).subscribe((result) => {
        const salesRep = result;
        if (salesRep) {
          this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '400px',
            height: '150px',
            data: { rep: salesRep },
          });
        }
      });
    }
  }

  public showWarrantyTextLink(): boolean {
    return !!(
      (this.order().leadTimeCode === 'WAR' || this.order().leadTimeCode === 'CPW' || this.order().leadTimeCode === 'RPW') &&
      this.order().warrantyRequestNotes &&
      this.order().warrantyRequestNotes.length > 0
    );
  }

  public showWarrantyText(): void {
    this.dialog.open(WarrantyTextDialogComponent, {
      disableClose: true,
      width: '400px',
      height: '250px',
      data: { warrantyText: this.order().warrantyRequestNotes },
    });
  }

  public refreshIecContact(): void {
    if (this.order().leadTimeCode === 'PTS') {
      this.selectionService.getPartsCustomerServiceReps().subscribe((results) => {
        this.iecContacts.set(results);
        if (this.iecContacts() && this.iecContacts().length > 0) {
          this.order().iecContactId = this.iecContacts()[0].code;
          this.order().iecContactDescription = this.iecContacts()[0].description;
        }
      });
    } else if (this.currentLeadTime() === 'PTS') {
      this.organizationService.getOrganization(this.order().repOfficeId).subscribe((sellingOrg) => {
        this.order().iecContactId = sellingOrg.serviceRepId;
        this.personService.getPersonById(this.order().iecContactId).subscribe((person) => {
          this.order().iecContactDescription = person.description;
        });
      });
      this.selectionService.getCustomerServiceReps().subscribe((results) => (this.iecContacts.set(results)));
    }
    this.currentLeadTime.set(this.order().leadTimeCode);
  }

  public selectSkyportContact(): void {
    this.entityService.hasSkyportCharacteristic(this.order().id).subscribe((result) => {
      if (result && result === 'true') {
        this.orderService.save(this.order()).subscribe({
          next: (result) => {
            this.order.set(result.updated);
            this.saveMessages.set(result.messages);
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
        this.showSkyportContactDialog();
      }
    });
  }

  private showSkyportContactDialog(): void {
    const skyportDialog = this.dialog.open(SkyportContactDialogComponent, {
      data: { entity: JSON.parse(JSON.stringify(this.order())) },
      width: '550px',
      height: '380px',
    });
    skyportDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.order.set(result);
      }
    });
  }

  public cancel(): void {
    const messages: string[] = ['This action will cancel this order. Do you want to proceed?'];
    this.dialogService.showConfirmationDialog(messages, 'Yes', 'No').subscribe({
      next: (confirmed: boolean) => {
        if (confirmed) {
          this.resetStatus();
          this.isBusy.set(true);
          this.orderService.cancel(this.order().id).subscribe({
            next: (result: Entity) => {
              this.order.set(result);
              this.isBusy.set(false);
              this.loadUpdateUser();
            },
            error: (error) => this.handleError(error),
          });
        }
      },
    });
  }

  public fixPermissions(): void {
    this.resetStatus();
    this.entityService.fixPermissions(this.order().id).subscribe({
      next: () => (this.isBusy.set(false)),
      error: (error) => this.handleError(error),
    });
  }

  public showSfrActions(): boolean {
    return this.order().updateAllowed && this.order().projectProductType != ProjectProductType.RES;
  }

  public canSubmitSfrs(): boolean {
    return this.order().updateAllowed && this.isBlanket();
  }

  private statusIn(codes: string[]): boolean {
    return codes.indexOf(this.order().statusDescription, 0) > -1;
  }

  public canCancelSfrs(): boolean {
    const applicableCodes: string[] = ['SFR Review'];
    return this.order().updateAllowed && this.statusIn(applicableCodes);
  }

  public sfrManagement(): void {
    this.router.navigate(['sfrManagement/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
  }

  public submitSfr(): void {
    if (this.errorMessages().length === 0) {
      this.resetStatus();
      this.sfrService.createRequest(this.order().id).subscribe({
        next: (result) => {
          this.order.set(result);
          this.isBusy.set(false);
          this.loadUpdateUser();
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  public cancelSfr(): void {
    this.resetStatus();
    this.sfrService.cancelRequest(this.order().id).subscribe({
      next: (result) => {
        this.order.set(result);
        this.isBusy.set(false);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  generateRevision(): void {
    const messages: string[] = [];
    messages.push('This action will generate a revision to this order.');
    messages.push('After the necessary changes are completed, a description for each change will be required.');
    messages.push('');
    messages.push('Do you want to proceed?');
    this.dialogService.showConfirmationDialog(messages, 'Yes', 'No').subscribe({
      next: (response: boolean) => {
        if (response) {
          this.resetStatus();
          this.orderService.generateRevision(this.order().id).subscribe({
            next: (result) => {
              this.viewOrder(result.id);
            },
            error: (error) => this.handleError(error),
          });
        }
      },
    });
  }

  getRevisionChangeReport() {
    this.isBusy.set(true);
    this.documentService.getRevisionChangeReport(this.order().id).subscribe({
      next: (result: any) => {
        FileUtils.downloadFile(result, this.order().id.entityId + 'RevisionChangeReport.xlsx');
        this.isBusy.set(false);
      },
      error: (error) => this.handleError(error),
    });
  }

  isBlanket(): boolean {
    const blanketPattern = /^(?:MR|SR)\d{8}$/g;
    return RegExp(blanketPattern).test(this.order().id.entityId);
  }

  showIecRelease(): boolean {
    const validApprovals: string[] = ['Pending Release', 'CSR Review'];
    return validApprovals.indexOf(this.order().approvalStatus, 0) > -1 && this.userCanReview();
  }

  releasePendingApproval(): void {
    this.resetStatus();
    this.orderService.releasePendingApproval(this.order().id).subscribe({
      next: (result) => {
        this.isBusy.set(false);
        this.order.set(result);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  userCanReview(): boolean {
    switch (this.order().reviewStatus) {
      case 'Admin Review':
        return this.personLoginService.hasRole(RoleName.IT);
      case 'Assign Folder Number':
      case 'SABR Review':
        return this.personLoginService.hasRole(RoleName.IT) || this.personLoginService.hasRole(RoleName.CredAnalyst);
      case 'CSR Review':
      case 'Valid':
        return this.isCustomerServiceOrIT();
      default:
        return false;
    }
  }

  isCustomerServiceOrIT() {
    return this.personLoginService.hasRole(RoleName.IT) || this.personLoginService.hasRole(RoleName.CustomerService);
  }

  canRelease(): boolean {
    const validApprovals: string[] = ['Approved - Need Releases', 'Approved'];
    return this.isBlanket() && this.order().statusDescription != 'Subsequent Revision' && validApprovals.indexOf(this.order().approvalStatus, 0) > -1;
  }

  canScheduleRelease(): boolean {
    const validPrefixes = ['MR', 'SR'];
    return this.canRelease() && validPrefixes.some((prefix) => this.order()?.id?.entityId || ''.startsWith(prefix));
  }

  readyForScheduling(): boolean {
    return !this.isBlanket() && 'BOOKED' === this.order().milestone && 'Approved' === this.order().approvalStatus && this.isCustomerServiceOrIT();
  }

  sendToScheduling(): void {
    this.resetStatus();
    this.orderService.sendToScheduling(this.order().id).subscribe({
      next: (result) => {
        this.isBusy.set(false);
        this.saveMessages.set([result]);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  isAcknowledgmentReady(): boolean {
    const earliest: Date = new Date(1900, 0, 1);
    const scheduleDate: Date = new Date(this.order().estimatedScheduleDate);
    return 'Approved' === this.order().approvalStatus && earliest < scheduleDate;
  }

  sendAcknowledgment(): void {
    this.resetStatus();
    this.orderService.sendAcknowledgment(this.order().id).subscribe({
      next: (result) => {
        this.isBusy.set(false);
        this.saveMessages.set([result]);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  public generateRelease(): void {
    this.router.navigate(['orderRelease/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
  }

  public scheduleReleases(): void {
    this.router.navigate(['scheduleRelease/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
  }

  isOrderReviewable(): boolean {
    const invalidStatuses: string[] = ['Cancelled', 'Closed', 'Subsequent Revision'];
    return this.order().updateAllowed && invalidStatuses.indexOf(this.order().statusDescription) === -1 && 'Pending Release' === this.order().approvalStatus;
  }

  openUnitTaggingScreen(): void {
    this.router.navigate(['unitTagging', this.order().id.entityName, this.order().id.entityId, this.order().id.entityRevision]);
  }

  getIncludeShippingTagsTooltip(): string {
    const frozenOrApproved: boolean = this.order().frozen || this.order().approvalStatus.indexOf('Approved') >= 0;
    if (frozenOrApproved) {
      if (this.order().includeShippingTags) {
        return 'Click the Tagging button to supply shipping tag information';
      } else {
        return 'You will need to generate a revision to add shipping tags';
      }
    } else {
      return 'Click the Tagging button to set this value and supply shipping tag information';
    }
  }

  openProjectDefaultsScreen(): void {
    this.router.navigate(['projectDefaults', this.order().id.entityName, this.order().id.entityId, this.order().id.entityRevision]);
  }

  openRiserChartDialog(): void {
    const riserDialog = this.dialog.open(RiserChartDialogComponent, {
      disableClose: true,
      width: '800px',
      maxWidth: '90vw',
      height: '400px',
      data: { entityKey: this.order().id },
    });
    riserDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isBusy.set(true);
        this.entityService.getEntityByKey(this.order().id).subscribe({
          next: (result) => {
            this.order.set(result);
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  canAddLines(): boolean {
    return this.order() && 'OR' != this.order().releaseTypeId && !this.order().frozen && this.order().approvalStatus.indexOf('Approved') < 0 && !this.isStockProgram();
  }

  isErpLocked(): boolean {
    return ['Entered', 'Shipped', 'PartialShp', 'DbWait', 'DbXferred'].includes(this.order()?.erpStatus);
  }

  canAddRisers(): boolean {
    return this.canAddLines() && BrandID.CLM !== this.order()?.brandID;
  }

  viewProcessingStatus(): void {
    this.dialog.open(ProcessingStatusDialogComponent, {
      width: '70vw',
      height: '300px',
      data: { entityKey: this.order().id },
    });
  }

  getReleaseType(): string {
    const filtered = this.releaseTypes().filter((releaseType) => releaseType.code === this.order().releaseTypeId);
    return filtered && filtered.length === 1 ? filtered[0].description : 'Unknown';
  }

  getComplexityCategory(): string {
    return this.isComplexityCategoryAssigned() ? this.entityComplexity().complexityCategory.category : 'Unknown';
  }

  showComplexityCategory(): boolean {
    return !this.entityService.isPartsOrWarranty(this.order());
  }

  private isComplexityCategoryAssigned(): boolean {
    return !!(
      this.entityComplexity() &&
      this.entityComplexity().complexityCategory &&
      this.entityComplexity().complexityCategory.category &&
      this.entityComplexity().complexityCategory.category.trim().length > 0
    );
  }

  // getComplexityLeadTime(): string {
  //   return this.isComplexityCategoryAssigned() ? this.entityComplexity().complexityCategory.leadTimeWeeks + ' weeks' : 'Unknown';
  // }

  // updateEntityComplexity(): void {
  //   this.isBusy.set(true);
  //   this.errorMessage = undefined;
  //   this.entityComplexityService.updateEntityComplexity(this.order().id).subscribe(complexity => {
  //     this.isBusy.set(false);
  //     this.entityComplexity.set(complexity);
  //   }, error => this.handleError(error));
  // }

  showComplexityInformationDialog(): void {
    this.dialog.open(ComplexityInfoDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      height: '300px',
      data: { entity: this.order(), complexity: this.entityComplexity() },
    });
  }

  canUpdateExtendedWarranty(): boolean {
    return ExtendedWarrantyUtils.canWarrantyBeChanged(this.order());
  }

  showExtendedWarrantyDialog(): void {
    this.dialogService.showExtendedWarrantyDialog(this.order()).subscribe({
      next: (result: boolean) => {
        if (result) {
          this.isBusy.set(true);
          this.entityService.getEntityByKey(this.order().id).subscribe({
            next: (entity) => {
              this.order.set(entity);
              this.isBusy.set(false);
            },
            error: (error) => this.handleError(error),
          });
        }
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  openQewlDialog(): void {
    this.dialog.open(QewlDialogComponent, {
      width: '1360px',
      height: '650px',
      data: this.order(),
    });
  }

  openCostReport(): void {
    this.router.navigate(['entityCostReport', this.order().id.entityId, this.order().id.entityRevision]);
  }

  isInternalOrRsm(): boolean {
    return this.personLoginService.isInternalOrRsm();
  }

  openSureFlowLoopSimulator(): void {
    this.router.navigate(['orders', 'sureflow', 'loops', this.order().id.entityId, this.order().id.entityRevision]);
  }

  isSureFlowUnit(line: EntityLineHeader): boolean {
    const productId = line?.product || '';
    return productId.toUpperCase().indexOf('SUREFLOW') >= 0;
  }

  isSureFlowUnitSelected(): boolean {
    if (!this.selectedLines || this.selectedLines.length === 0) {
      return false;
    }
    return this.selectedLines.filter((line) => this.isSureFlowUnit(line)).length > 0;
  }

  public enterContact(isPrimaryContact: boolean): void {
    const dialog = this.dialog.open(EnterContactDialogComponent, {
      data: { isPrimaryContact: isPrimaryContact, entity: this.order() },
    });
    dialog.afterClosed().subscribe((result) => {
      if (result) {
        if (result instanceof HttpErrorResponse) {
          this.handleError(result);
        } else {
          this.order.set(result);
        }
      }
    });
  }

  canSubmitForReview(): boolean {
    return this.isOrderReviewable() && !this.isModReviewTaskOpen();
  }

  canSubmitOrder(): boolean {
    return this.order().erpStatus === 'CustApprvl';
  }

  submitOrderToBackEndSystemsAfterScheduledReleaseDatesApplied() {
    this.isBusy.set(true);
    this.order().erpStatus = 'DbWait';
    this.orderService.save(this.order()).subscribe({
      next: () => (this.isBusy.set(false)),
      error: (error) => this.handleError(error),
    });
  }

  createErrorMessage(): string {
    let errorMessage = '';
    for (const message of this.errorMessages()) {
      errorMessage = errorMessage + message + '\n';
    }
    return errorMessage;
  }

  showFpaManagementButton(): boolean {
    return this.canSubmitFpa() && !LeadTimeUtils.isStockProgram(this.order().leadTimeCode);
  }

  showFpaCancelButton(): boolean {
    return this.canCancelFpaReview();
  }

  private updateFpaStatusFlags(): void {
    this.canCancelFpaReview.set(false);
    this.canSubmitFpa.set(false);
    if (StringUtils.isNotBlank(this.order()?.id?.entityId)) {
      const fpaStatus: string = this.order()?.fpaStatus || 'None';
      this.fpaService.isPendingApproval(fpaStatus).subscribe({
        next: (response: string) => {
          this.canCancelFpaReview.set(StringUtils.equalsIgnoreCase('true', response));
          this.canSubmitFpa.set(!this.order().frozen && !this.canCancelFpaReview());
        },
        error: (error: any) => this.handleError(error),
      });
    }
  }

  cancelFpaReview(): void {
    this.isBusy.set(true);
    this.fpaService.cancelSubmission(EntityKeyUtils.convertToFullEntityKey(this.order().id)).subscribe({
      next: () => {
        this.entityService.getEntityByKey(this.order().id).subscribe({
          next: (result: Entity) => {
            this.order.set(result);
            this.isBusy.set(false);
            this.updateFpaStatusFlags();
          },
          error: (error) => this.handleError(error),
        });
      },
      error: (error: any) => this.handleError(error),
    });
  }

  determineClmOrderid(): string {
    let clmOrderId = '';
    if (
      this.order() &&
      this.order().projectNumber &&
      this.order().projectProductType === ProjectProductType.COM &&
      this.order().leadTimeCode != 'CSP' &&
      this.order().leadTimeCode != 'CPW'
    ) {
      clmOrderId = 'EZ-'.concat(this.order().projectNumber);
    } else if (
      this.order() &&
      this.order().projectNumber &&
      this.order().projectProductType === ProjectProductType.COM &&
      (this.order().leadTimeCode === 'CSP' || this.order().leadTimeCode === 'CPW')
    ) {
      clmOrderId = this.order().projectNumber;
    } else if (this.order() && this.order().projectNumber && this.order().projectProductType === ProjectProductType.RES) {
      clmOrderId = this.order().projectNumber;
    }

    return clmOrderId;
  }

  shipViaChange(shipViaValue: string) {
    if (shipViaValue.includes('CPU') || shipViaValue.includes('SCA') || shipViaValue.includes('3PB')) {
      this.order().shipAndHandChargeType = 'CPU';
    } else {
      this.order().shipAndHandChargeType = 'PPA';
    }
  }

  public showSpecialQuotes(): boolean {
    return (
      !LeadTimeUtils.isStockProgram(this.order().leadTimeCode) &&
      this.order().updateAllowed &&
      this.order().projectProductType != ProjectProductType.RES &&
      !!this.order().lines &&
      this.order().lines.length > 0
    );
  }

  public specialQuotes(): void {
    if (!['COM', 'CSP'].includes(this.order().leadTimeCode)) {
      this.errorMessages.update(current => [...current, 'Special Quotes are only available with a Standard Lead Time or Standard Replacement Parts. To add a Special Feature you will need to change the lead time to Standard Lead Time or Standard Replacement Parts']);
    } else {
      this.dialogService.showSpecialQuotesDialog(this.order().id, this.personLogin, this.order().brandID).subscribe({
        next: (result) => {
          this.entityService.getEntityByKey(this.order().id).subscribe({
            next: (result: Entity) => {
              this.order.set(result);
            },
            error: (error: HttpErrorResponse) => this.handleError(error),
          });
        },
        error: (error) => {this.handleError(error);}
      });
    }
  }

  showEntityHistoryDialog(): void {
    if (this.order()) {
      this.dialogService.showEntityHistoryDialog(this.order());
    }
  }

  showUnrestrictedEditOption(): boolean {
    return this.order() && this.order().updateAllowed && this.order().frozen && this.canUpdateInternalNotes();
  }

  canUpdateInternalNotes(): boolean {
    return this.userHasRole("CustomerService") || this.userHasRole("IT") || this.userHasRole("CredAnalyst") || this.userHasRole("ApplicationEngineer") || this.userHasRole("RSM");
  }

  showUnrestrictedChangesDialog(): void {
    if (this.showUnrestrictedEditOption()) {
      this.dialogService.showUnrestrictedEntityUpdatesDialog(this.order()).subscribe({
        next: (update: UnrestrictedEntityUpdate) => {
          if (update) {
            this.isBusy.set(true);
            this.errorMessages.set([]);
            this.entityService.saveEntityWithoutRestrictions(update).subscribe({
              next: (updatedEntity: Entity) => {
                this.order.set(updatedEntity);
                this.isBusy.set(false);
              }, error: (error: any) => this.handleError(error)
            });
          }
        }, error: (error: any) => this.handleError(error)
      });
    }
  }

  protected readonly LeadTimeUtils = LeadTimeUtils;

  private initializeFromRouteParams(): void {
    const idString: string = this.entityId();
    const revisionString: string = this.entityRevision();
    if (idString && idString.length > 0 && !isNaN(+revisionString)) {
      const key: EntityKey = new EntityKey();
      key.entityId = idString;
      key.entityName = 'CONTRACT';
      key.entityRevision = +revisionString;
      this.orderService.isRepriceNeeded(key).subscribe({
        next: (repriceNeeded) => {
          if (repriceNeeded && repriceNeeded == 'true') {
            this.waitMessage.set('Updating List Prices...');
            this.isBusy.set(true);
            this.orderService.reprice(key).subscribe({
              next: (result) => this.loadOrderDetails(result),
              error: (error) => this.handleError(error),
            });
          } else {
            this.entityService.getEntity('CONTRACT', idString, +revisionString).subscribe({
              next: (result: Entity) => this.loadOrderDetails(result),
              error: (error) => this.handleError(error),
            });
          }
        },
        error: (error) => this.handleError(error),
      });
    } else {
      this.backToOrderMaster();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initializeFromRouteParams();
    }
  }
}
